/**
 * Time-zone math without dependencies (Intl only). All instants are epoch milliseconds (UTC);
 * "local" means wall-clock time in an IANA time zone such as Europe/Zurich.
 */

export type LocalDate = { year: number; month: number; day: number } // month: 1-12

export type ZonedParts = LocalDate & {
  hour: number
  minute: number
  second: number
  /** 0 = Sunday … 6 = Saturday */
  weekday: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    formatters.set(timeZone, f)
  }
  return f
}

export function weekdayOf(date: LocalDate): number {
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay()
}

/** Wall-clock parts of an instant in a time zone. */
export function zonedParts(instantMs: number, timeZone: string): ZonedParts {
  const values: Record<string, number> = {}
  for (const part of formatter(timeZone).formatToParts(new Date(instantMs))) {
    if (part.type !== 'literal') values[part.type] = Number(part.value)
  }
  const date = { year: values.year ?? 1970, month: values.month ?? 1, day: values.day ?? 1 }
  return {
    ...date,
    hour: (values.hour ?? 0) % 24,
    minute: values.minute ?? 0,
    second: values.second ?? 0,
    weekday: weekdayOf(date),
  }
}

const offsetCache = new Map<string, number>()
const QUARTER_HOUR_MS = 15 * 60_000

/**
 * Offset of local time from UTC at an instant (local − UTC, in ms). Cached per 15-minute bucket:
 * real-world offset changes happen on 15-minute UTC boundaries.
 */
export function tzOffsetMs(instantMs: number, timeZone: string): number {
  const key = `${timeZone}|${Math.floor(instantMs / QUARTER_HOUR_MS)}`
  const cached = offsetCache.get(key)
  if (cached !== undefined) return cached
  const p = zonedParts(instantMs, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  const offset = asUtc - Math.floor(instantMs / 1000) * 1000
  if (offsetCache.size > 50_000) offsetCache.clear()
  offsetCache.set(key, offset)
  return offset
}

/** The instant of a wall-clock time (minutes after local midnight) on a local date. */
export function zonedToUtc(date: LocalDate, minutesOfDay: number, timeZone: string): number {
  const naive = Date.UTC(date.year, date.month - 1, date.day, 0, minutesOfDay)
  const first = tzOffsetMs(naive, timeZone)
  let utc = naive - first
  const second = tzOffsetMs(utc, timeZone)
  if (second !== first) utc = naive - second
  return utc
}

export function localDateOf(instantMs: number, timeZone: string): LocalDate {
  const { year, month, day } = zonedParts(instantMs, timeZone)
  return { year, month, day }
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
}

export function formatLocalDate(date: LocalDate): string {
  const mm = String(date.month).padStart(2, '0')
  const dd = String(date.day).padStart(2, '0')
  return `${date.year}-${mm}-${dd}`
}

export function parseLocalDate(text: string): LocalDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (!match) throw new Error(`invalid date "${text}"`)
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day
}

export function startOfLocalDay(date: LocalDate, timeZone: string): number {
  return zonedToUtc(date, 0, timeZone)
}

/** Last millisecond of a local day (23:59:59.999, DST-safe). */
export function endOfLocalDay(date: LocalDate, timeZone: string): number {
  return startOfLocalDay(addDays(date, 1), timeZone) - 1
}

/** Start of the local week containing an instant. `weekStart`: 1 = Monday, 0 = Sunday. */
export function startOfLocalWeek(instantMs: number, timeZone: string, weekStart: 0 | 1): number {
  const today = localDateOf(instantMs, timeZone)
  const back = (weekdayOf(today) - weekStart + 7) % 7
  return startOfLocalDay(addDays(today, -back), timeZone)
}

/** Minutes after midnight for "HH:MM". */
export function parseHhmm(text: string): number {
  const [h, m] = text.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}
